// =====================================================
// SERVIÇO DE TOOLS DO GOOGLE CALENDAR PARA IAs
// Expõe funções do Calendar como tools nativas da OpenAI
// =====================================================

import { googleCalendarService } from './google-calendar-service';

class CalendarToolsService {
  /**
   * Retorna as tools do Google Calendar para a API da OpenAI
   */
  getCalendarTools() {
    return [
      {
        type: 'function',
        function: {
          name: 'calendar_list_events',
          description: 'Lista os próximos eventos do Google Calendar do usuário. Use para consultar agenda, compromissos, reuniões.',
          parameters: {
            type: 'object',
            properties: {
              maxResults: {
                type: 'number',
                description: 'Número máximo de eventos a retornar (padrão: 10)'
              },
              timeMin: {
                type: 'string',
                description: 'Data/hora mínima no formato ISO (ex: 2025-01-01T00:00:00Z). Padrão: agora'
              },
              timeMax: {
                type: 'string',
                description: 'Data/hora máxima no formato ISO. Opcional.'
              }
            },
            required: []
          }
        }
      },
      {
        type: 'function',
        function: {
          name: 'calendar_create_event',
          description: 'Cria um novo evento no Google Calendar do usuário. Use para agendar reuniões, compromissos, lembretes.',
          parameters: {
            type: 'object',
            properties: {
              summary: {
                type: 'string',
                description: 'Título/nome do evento'
              },
              description: {
                type: 'string',
                description: 'Descrição detalhada do evento (opcional)'
              },
              start_datetime: {
                type: 'string',
                description: 'Data e hora de início no formato ISO (ex: 2025-12-20T14:00:00)'
              },
              end_datetime: {
                type: 'string',
                description: 'Data e hora de fim no formato ISO (ex: 2025-12-20T15:00:00)'
              },
              location: {
                type: 'string',
                description: 'Local do evento (opcional)'
              }
            },
            required: ['summary', 'start_datetime', 'end_datetime']
          }
        }
      }
    ];
  }

  /**
   * Executa uma operação do calendar
   */
  async executeCalendarTool(
    functionName: string, 
    args: any, 
    userId: string
  ): Promise<{ success: boolean; result?: any; error?: string; formattedResponse: string }> {
    try {
      console.log(`🗓️ Executando ${functionName} com args:`, args);

      if (functionName === 'calendar_list_events') {
        const events = await googleCalendarService.listEvents(userId, {
          maxResults: args.maxResults || 10,
          timeMin: args.timeMin,
          timeMax: args.timeMax
        });

        if (!events || events.length === 0) {
          return {
            success: true,
            result: [],
            formattedResponse: '📅 Nenhum evento encontrado no período solicitado.'
          };
        }

        // Formatar os eventos de forma legível
        const formattedEvents = events.map((event: any, index: number) => {
          const start = event.start?.dateTime || event.start?.date;
          const startDate = new Date(start);
          const dateStr = startDate.toLocaleDateString('pt-BR', { 
            weekday: 'short', 
            day: '2-digit', 
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
          return `${index + 1}. **${event.summary || 'Sem título'}** - ${dateStr}`;
        }).join('\n');

        return {
          success: true,
          result: events,
          formattedResponse: `📅 **Seus próximos eventos:**\n\n${formattedEvents}`
        };
      }

      if (functionName === 'calendar_create_event') {
        const event = await googleCalendarService.createEvent(userId, {
          summary: args.summary,
          description: args.description,
          start: args.start_datetime,
          end: args.end_datetime,
          location: args.location
        });

        const startDate = new Date(args.start_datetime);
        const dateStr = startDate.toLocaleDateString('pt-BR', { 
          weekday: 'long', 
          day: '2-digit', 
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });

        return {
          success: true,
          result: event,
          formattedResponse: `✅ **Evento criado com sucesso!**\n\n📌 **${args.summary}**\n📅 ${dateStr}${args.location ? `\n📍 ${args.location}` : ''}${args.description ? `\n📝 ${args.description}` : ''}`
        };
      }

      return {
        success: false,
        error: `Função desconhecida: ${functionName}`,
        formattedResponse: `❌ Erro: função ${functionName} não reconhecida.`
      };

    } catch (error) {
      console.error(`❌ Erro ao executar ${functionName}:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      return {
        success: false,
        error: errorMessage,
        formattedResponse: `❌ Erro ao executar ${functionName}: ${errorMessage}`
      };
    }
  }
}

export const calendarToolsService = new CalendarToolsService();

